import { metricKeys, type CaptureProfile, type MetricKey } from '@/domain/types'

export const MIN_CAPTURE_FPS = 1
export const DEFAULT_CAPTURE_FPS = 2
export const DEFAULT_EXP_CAPTURE_FPS = DEFAULT_CAPTURE_FPS
export const DEFAULT_MESOS_CAPTURE_FPS = DEFAULT_CAPTURE_FPS
export const MAX_CAPTURE_FPS = 8
export const METRIC_CAPTURE_FPS_VERSION = 5

const RECOGNITION_INTERVAL_REFERENCE_FPS = 4
const PREVIOUS_DEFAULT_CAPTURE_FPS = 4
const PREVIOUS_EXP_CAPTURE_FPS = 8
const PREVIOUS_MESOS_CAPTURE_FPS = 8

export type MetricCaptureFpsOverrides = Partial<Record<MetricKey, number>>

export function normalizeCaptureFps(value: number | undefined): number {
  if (!Number.isFinite(value)) return DEFAULT_CAPTURE_FPS
  return Math.min(MAX_CAPTURE_FPS, Math.max(MIN_CAPTURE_FPS, Math.round(value!)))
}

export function captureIntervalMs(captureFps: number | undefined): number {
  return Math.round(1_000 / normalizeCaptureFps(captureFps))
}

export function scaleRecognitionIntervalMs(baseIntervalMs: number, captureFps: number | undefined): number {
  return Math.max(1, Math.round(baseIntervalMs * RECOGNITION_INTERVAL_REFERENCE_FPS / normalizeCaptureFps(captureFps)))
}

export function normalizeMetricCaptureFps(
  values: Readonly<Partial<Record<MetricKey, number>>> | undefined,
): MetricCaptureFpsOverrides {
  const normalized: MetricCaptureFpsOverrides = {}
  for (const metric of metricKeys) {
    const value = values?.[metric]
    if (Number.isFinite(value)) normalized[metric] = normalizeCaptureFps(value)
  }
  return normalized
}

export function recommendedMetricCaptureFps(): Record<MetricKey, number> {
  const defaults = Object.fromEntries(
    metricKeys.map((metric) => [metric, DEFAULT_CAPTURE_FPS]),
  ) as Record<MetricKey, number>
  defaults.exp = DEFAULT_EXP_CAPTURE_FPS
  defaults.mesos = DEFAULT_MESOS_CAPTURE_FPS
  return defaults
}

export function migrateRecognitionFrequencies(
  recognition: CaptureProfile['recognition'],
): CaptureProfile['recognition'] {
  let captureFps = normalizeCaptureFps(recognition.captureFps)
  const defaults = recommendedMetricCaptureFps()
  const version = recognition.metricCaptureFpsVersion ?? 0
  const normalized = normalizeMetricCaptureFps(recognition.metricCaptureFps)
  const metricCaptureFps = { ...defaults }

  if (version >= 2) {
    for (const metric of metricKeys) {
      const value = normalized[metric]
      if (value === undefined) continue
      const previousDefault = previousMetricDefault(version, metric)
      metricCaptureFps[metric] = version < METRIC_CAPTURE_FPS_VERSION && value === previousDefault
        ? defaults[metric]
        : value
    }
  }
  if (version < METRIC_CAPTURE_FPS_VERSION && captureFps === PREVIOUS_DEFAULT_CAPTURE_FPS) {
    captureFps = DEFAULT_CAPTURE_FPS
  }
  return {
    ...recognition,
    captureFps,
    metricCaptureFps,
    metricCaptureFpsVersion: METRIC_CAPTURE_FPS_VERSION,
  }
}

function previousMetricDefault(version: number, metric: MetricKey): number {
  if (metric === 'exp' && version >= 3) return PREVIOUS_EXP_CAPTURE_FPS
  if (metric === 'mesos' && version >= 4) return PREVIOUS_MESOS_CAPTURE_FPS
  return PREVIOUS_DEFAULT_CAPTURE_FPS
}

export function effectiveMetricCaptureFps(
  recognition: CaptureProfile['recognition'],
  metric: MetricKey,
): number {
  const override = recognition.metricCaptureFps?.[metric]
  return Number.isFinite(override) ? normalizeCaptureFps(override) : normalizeCaptureFps(recognition.captureFps)
}

export function maxRecognitionCaptureFps(recognition: CaptureProfile['recognition']): number {
  return metricKeys.reduce(
    (maximum, metric) => Math.max(maximum, effectiveMetricCaptureFps(recognition, metric)),
    MIN_CAPTURE_FPS,
  )
}

export function scaleMetricRecognitionIntervalMs(
  baseIntervalMs: number,
  recognition: CaptureProfile['recognition'],
  metric: MetricKey,
): number {
  return scaleRecognitionIntervalMs(baseIntervalMs, effectiveMetricCaptureFps(recognition, metric))
}
